import type { StatusTone } from '@/components/ui/status-pill';

/**
 * Where a banner appears — and, implicitly, who sees it.
 *
 * The first four are surfaces in the customer app. The `VENDOR_` ones are inside the
 * vendor console, for announcements aimed at sellers: a fee change, a policy update, a
 * seasonal push to stock up. The two audiences are served by different endpoints, so a
 * shopper is never handed a message written for a shopkeeper.
 */
export const BANNER_PLACEMENTS = [
  'HOME',
  'FOOD_CAFE',
  'SERVICES',
  'HEALTH',
  'VENDOR_HOME',
  'VENDOR_ONBOARDING',
] as const;
export type BannerPlacement = (typeof BANNER_PLACEMENTS)[number];

export type BannerAudience = 'CUSTOMER' | 'VENDOR';

/**
 * Derived from the placement, never stored alongside it.
 *
 * A second field holding the same fact is a second field that can disagree with the
 * first — and the way a customer ends up reading "your payout schedule is changing".
 */
export function audience_of(p: BannerPlacement): BannerAudience {
  return p.startsWith('VENDOR_') ? 'VENDOR' : 'CUSTOMER';
}

export function placements_for(a: BannerAudience): BannerPlacement[] {
  return BANNER_PLACEMENTS.filter((p) => audience_of(p) === a);
}

export const PLACEMENT_LABEL: Record<BannerPlacement, string> = {
  HOME: 'App home',
  FOOD_CAFE: 'Food & cafés',
  SERVICES: 'Services',
  HEALTH: 'Health',
  VENDOR_HOME: 'Vendor dashboard',
  VENDOR_ONBOARDING: 'Vendor onboarding',
};

export const AUDIENCE_LABEL: Record<BannerAudience, string> = {
  CUSTOMER: 'Shoppers',
  VENDOR: 'Vendors',
};

export const BANNER_LINK_TYPES = ['NONE', 'VENDOR', 'MODULE', 'URL'] as const;
export type BannerLinkType = (typeof BANNER_LINK_TYPES)[number];

/**
 * What a banner may link to, per audience.
 *
 * A vendor looking at their own dashboard is not shopping, so a link to a storefront or a
 * customer module would be a dead tap. The backend refuses these too; the console simply
 * does not offer them.
 */
export const LINK_TYPES_FOR: Record<BannerAudience, readonly BannerLinkType[]> = {
  CUSTOMER: ['NONE', 'VENDOR', 'MODULE', 'URL'],
  VENDOR: ['NONE', 'URL'],
};

export const LINK_TYPE_LABEL: Record<BannerLinkType, string> = {
  NONE: 'Not tappable',
  VENDOR: 'Opens a vendor',
  MODULE: 'Opens a module',
  URL: 'Opens a link',
};

import type { VendorModuleType } from '@/features/vendors/types';

export type AdminBanner = {
  id: string;
  placement: BannerPlacement;
  format: BannerFormat;
  /** Derived server-side from the placement — see `audience_of`. */
  audience: BannerAudience;
  title: string;
  image_path: string;
  link_type: BannerLinkType;
  link_value: string | null;
  /**
   * Which vendor modules this banner is aimed at. Empty means every vendor.
   *
   * VENDOR placements only — a shopper has no modules, so the server ignores it on a customer
   * placement rather than pretending it means something there.
   */
  target_modules: VendorModuleType[];
  display_order: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * How the artwork is drawn. Applies to both audiences.
 *
 * A wide creative squeezed into an icon slot is unreadable and an icon stretched across a hero
 * is a blurry mess, so only whoever made the artwork can say which it is.
 */
export const BANNER_FORMATS = ['BANNER', 'ICON'] as const;
export type BannerFormat = (typeof BANNER_FORMATS)[number];

export const BANNER_FORMAT_LABEL: Record<BannerFormat, string> = {
  BANNER: 'Full-width banner',
  ICON: 'Small icon tile',
};

export type BannerListResponse = { items: AdminBanner[] };

export type CreateBannerInput = {
  placement: BannerPlacement;
  format?: BannerFormat;
  title: string;
  image_s3_key: string;
  link_type: BannerLinkType;
  link_value?: string | null;
  target_modules?: VendorModuleType[];
  display_order?: number;
  is_active?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
};

export type UpdateBannerInput = Partial<CreateBannerInput>;

/** A banner is only on screen when active AND inside its window — mirror of the API rule. */
export function is_live(b: AdminBanner, now = new Date()): boolean {
  if (!b.is_active) return false;
  if (b.starts_at && new Date(b.starts_at) > now) return false;
  if (b.ends_at && new Date(b.ends_at) < now) return false;
  return true;
}

export type BannerStatus = 'Live' | 'Scheduled' | 'Expired' | 'Switched off';

export function status_label(b: AdminBanner, now = new Date()): BannerStatus {
  if (!b.is_active) return 'Switched off';
  if (b.starts_at && new Date(b.starts_at) > now) return 'Scheduled';
  if (b.ends_at && new Date(b.ends_at) < now) return 'Expired';
  return 'Live';
}

export const STATUS_TONE: Record<BannerStatus, StatusTone> = {
  Live: 'success',
  Scheduled: 'info',
  Expired: 'neutral',
  'Switched off': 'neutral',
};

/**
 * When a banner's window opens or closes, in words.
 *
 * The dates alone made an admin do date arithmetic to answer "is this one running" — the
 * question the screen exists to answer.
 */
export function window_summary(b: AdminBanner, now = new Date()): string {
  const from = b.starts_at ? new Date(b.starts_at) : null;
  const to = b.ends_at ? new Date(b.ends_at) : null;
  if (!from && !to) return 'No end date — runs until switched off';
  if (from && from > now) return `Starts ${from.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  if (to && to < now) return `Ended ${to.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  if (to) return `Until ${to.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  return 'Running, no end date';
}

/**
 * Does this banner's run overlap the given window?
 *
 * A banner's dates are a SCHEDULE, not a record timestamp, so "created between" would
 * answer a question nobody asks. What an admin wants to know is what was — or will be — on
 * screen during a period: which promotion was running when that campaign spiked.
 *
 * An open-ended banner (no start, no end, or one of the two) overlaps anything on that
 * side, which is what "runs until switched off" means.
 */
export function runs_during(b: AdminBanner, from: string, to: string): boolean {
  if (!from && !to) return true;
  const window_start = from ? new Date(`${from}T00:00:00.000Z`) : null;
  const window_end = to ? new Date(`${to}T23:59:59.999Z`) : null;
  const banner_start = b.starts_at ? new Date(b.starts_at) : null;
  const banner_end = b.ends_at ? new Date(b.ends_at) : null;

  if (window_end && banner_start && banner_start > window_end) return false;
  if (window_start && banner_end && banner_end < window_start) return false;
  return true;
}
